import { useSession } from '@/auth/useSession';
import { useTimeZone } from '@/auth/useTimeZone';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { useToast } from '@/components/ui/hooks/use-toast';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import { validateRouteRole } from '@/lib/routing';
import { isCoachAllStatuses } from '@airreading/common';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { BasicProfileForm } from './-components/basic-form';
import { CoachPage } from './-components/coach-page';

export const Route = createFileRoute('/_layout/profile/')({
  beforeLoad: validateRouteRole(isCoachAllStatuses),
  component: Profile,
});

export function Profile() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load your profile. Please try again.',
          back: {
            backLabel: 'Back to home',
            backLink: {
              to: '/',
            },
          },
        };
      }}
    >
      <ProfilePage />
    </InfoPageSuspenseBoundary>
  );
}

function ProfilePage() {
  const { actingUser } = useSession();
  const userIsCoach = useMemo(() => isCoachAllStatuses(actingUser), [actingUser]);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { setTimezone } = useTimeZone();

  const getSubmitErrorMessage = (e: unknown): string => {
    const message =
      airServerErrorFrom(e)?.userFacingMessage ??
      (e instanceof Error ? e.message : 'Unable to update profile. Please try again or contact Air Reading support.');

    return message;
  };

  const FormComponent = userIsCoach ? CoachPage : BasicProfileForm;

  return (
    <FormComponent
      toast={toast}
      queryClient={queryClient}
      setTimezone={setTimezone}
      getSubmitErrorMessage={getSubmitErrorMessage}
    />
  );
}
